import { useEffect, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Trans, useTranslation } from "react-i18next"

import apiClient from "src/apiClient"
import { selectRankingLeagues } from "src/store/common"
import { me, selectIsAuthenticated, selectRankingPosition, selectUserId } from "src/store/user"
import {
  ALL_LEAGUES,
  COMMANDER,
  DEFAULT_PER_PAGE,
  GAP,
  LEAGUE_ICONS,
  PER_PAGE_OPTIONS,
  formatPoints,
  leagueOrder,
  leagueRanges,
  pageNumbers,
} from "src/utils/ranking"

import { avatars } from "src/assets/images"
import { avatarFor } from "src/utils/cosmetics"
import LeagueFrame from "src/components/LeagueFrame"
import PlayerCardButton from "src/components/PlayerCardButton"
import BaseLayout from "src/layouts/BaseLayout"
import useLocalizedPath from "src/hooks/useLocalizedPath"

import "./Ranking.css"

const rowId = (userId) => `ranking-row-${userId}`

// The league's mark: its emoji (owner, 2026-10-08 — preferred to a drawn diamond).
const LeagueGlyph = ({ league }) => (
  <span className="league-emoji" aria-hidden="true">
    {LEAGUE_ICONS[league]}
  </span>
)

const Arrow = ({ back }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={back ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6"} />
  </svg>
)

/* The hall of fame's full list (owner, 2026-09-29; redesigned 2026-10-08): a search by name, a
   filter by league — one row, lowest first, Commander last, pressed again to show everybody — a switch
   for the players with no points, and pages. The server does all of it (`GET /ranking/`,
   `backend:users/leaderboard.py`), so every player is reachable, not only the top hundred the home
   page shows, and every row carries its OVERALL place and its league from there.

   It opens on the card that was pressed (`?focus=<id>`): the filters are cleared and the server is
   asked for the page that holds that player (`around`), whose row is lit. MY POSITION does the same
   for the signed-in player. The search waits `SEARCH_DELAY_MS` after the last key. */
const SEARCH_DELAY_MS = 250

// The request a set of choices makes; also how a held answer is matched to the choices it answers.
const paramsFor = ({ query, league, hideZero, page, perPage }) => ({
  q: query.trim(),
  league: league === ALL_LEAGUES ? "" : league,
  hide_zero: hideZero ? "1" : "",
  page,
  per_page: perPage,
})

const Ranking = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const localized = useLocalizedPath()
  const [searchParams, setSearchParams] = useSearchParams()
  const leagues = useSelector(selectRankingLeagues)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const myId = useSelector(selectUserId)
  const myPosition = useSelector(selectRankingPosition)

  const [query, setQuery] = useState("")
  const [searchText, setSearchText] = useState("")
  const [league, setLeague] = useState(ALL_LEAGUES)
  const [hideZero, setHideZero] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)
  // The answer held, and the request it answered: a newer answer always wins over an older one.
  const [data, setData] = useState(null)
  const [dataKey, setDataKey] = useState(null)
  const latest = useRef(0)
  // Bumped by every jump to a row, so a second press scrolls back even if the focus did not change.
  const [scrollRequest, setScrollRequest] = useState(0)

  const focus = searchParams.get("focus")
  const choices = { query, league, hideZero, page, perPage }
  const key = JSON.stringify(paramsFor(choices))

  const ask = async (params, around = null) => {
    const ticket = (latest.current += 1)
    const response = await apiClient.get("/ranking/", { params: around == null ? params : { ...params, around } })
    if (ticket !== latest.current) return null
    setData(response.data)
    setDataKey(JSON.stringify({ ...params, page: response.data.page }))
    return response.data
  }

  useEffect(() => {
    if (isAuthenticated) dispatch(me())
  }, [dispatch, isAuthenticated])

  // The search is sent once the typing stops.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchText), SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [searchText])

  // Any other change of choice asks at once; an answer already held for these choices is not asked for again.
  useEffect(() => {
    if (key === dataKey) return
    ask(paramsFor(choices)).catch(() => {})
    // `choices` is what `key` is made of.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const clearFilters = () => {
    setSearchText("")
    setQuery("")
    setLeague(ALL_LEAGUES)
    setHideZero(false)
    setPage(1)
  }

  // A focused player (a card pressed on the home page, or MY POSITION) is shown on their page with
  // nothing filtering them out: the server is asked for the page that holds them.
  useEffect(() => {
    if (focus == null) return
    const cleared = { query: "", league: ALL_LEAGUES, hideZero: false, page: 1, perPage }
    ask(paramsFor(cleared), focus)
      .then((answer) => {
        if (!answer) return
        clearFilters()
        setPage(answer.page)
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, scrollRequest])

  const current = data?.page ?? 1
  const pageCount = data?.page_count ?? 1
  const slice = (data?.results ?? []).map((row) => ({
    id: row.id,
    username: row.username,
    rankingPoints: row.ranking_points,
    rank: row.rank,
    league: row.league,
    equippedCosmetics: row.equipped_cosmetics,
  }))
  // The podium stands over the unfiltered first page only: anywhere else, the first three rows
  // shown are not the first three of the ranking.
  const podium =
    current === 1 && !query.trim() && league === ALL_LEAGUES && !hideZero ? slice.filter((row) => row.rank <= 3) : []
  const from = slice.length ? (current - 1) * (data?.per_page ?? perPage) + 1 : 0
  const to = from ? from + slice.length - 1 : 0

  useEffect(() => {
    if (focus == null) return
    const row = document.getElementById(rowId(focus))
    // Not there yet is fine: the answer arriving re-runs this. Arriving from a card jumps — the page
    // is fading in, and a smooth scroll is an animation a background tab never finishes — and a
    // press of MY POSITION glides, so the player sees which way their row was.
    row?.scrollIntoView?.({ block: "center", behavior: scrollRequest > 0 ? "smooth" : "auto" })
  }, [focus, data, scrollRequest])

  const goToMe = () => {
    setSearchParams({ focus: String(myId) }, { replace: true })
    setScrollRequest((n) => n + 1)
  }

  const order = leagueOrder(leagues)
  const ranges = Object.fromEntries(leagueRanges(leagues).map((range) => [range.id, range]))
  const leagueName = (id) => t(`ranking.leagues.${id}`)
  const rangeText = (id) => {
    if (id === COMMANDER) return t("ranking.commanderRange", { count: leagues?.commander_top })
    const range = ranges[id]
    if (!range) return ""
    return range.to == null ? t("ranking.leagueFrom", { from: range.from }) : t("ranking.leagueSpan", { from: range.from, to: range.to })
  }

  // One button a league and no "all" (owner, 2026-10-08): a second press on the league shown
  // shows everybody again. The counts are the server's, over the search and the zero switch.
  const chips = order.map((id) => ({ id, count: data?.league_counts?.[id] ?? 0 }))

  // A player's picture (owner, 2026-10-08), the one they chose on their profile.
  const look = (user) => (
    <span className="ranking-look">
      <img src={avatars[avatarFor(user)]} alt="" />
    </span>
  )

  const row = ({ id, username, rankingPoints, rank, league: rowLeague, equippedCosmetics }, extraClass = "") => {
    const classes = ["ranking-row", extraClass]
    if (rank <= 3) classes.push("podium")
    if (String(id) === String(focus)) classes.push("focused")
    if (String(id) === String(myId)) classes.push("mine")
    return (
      <li key={id} id={rowId(id)} className={classes.filter(Boolean).join(" ")}>
        <span className={`ranking-rank${rowLeague ? ` league-${rowLeague}` : ""}`}>{String(rank).padStart(2, "0")}</span>
        <span className="ranking-name">
          <PlayerCardButton user={{ id, username, league: rowLeague, rankingPoints }} picture={avatarFor({ equippedCosmetics })}>
            <LeagueFrame league={rowLeague} size="sm" tag={false} className="ranking-frame">
              {look({ equippedCosmetics })}
            </LeagueFrame>
          </PlayerCardButton>
          <span className="ranking-name-text">{username}</span>
          {String(id) === String(myId) && <span className="ranking-you">{t("ranking.you")}</span>}
        </span>
        <span className="ranking-league-cell">
          {rowLeague && (
            <span className={`league-badge league-${rowLeague}`}>
              <LeagueGlyph league={rowLeague} />
              {leagueName(rowLeague)}
            </span>
          )}
        </span>
        <span className="ranking-points">
          {formatPoints(rankingPoints)}
          <span className="ranking-pts">{t("ranking.pts")}</span>
        </span>
      </li>
    )
  }

  return (
    <BaseLayout>
      <div className="ranking-page with-scroll-bar vertical">
        <main className="ranking-main">
          <header className="ranking-header">
            <div className="ranking-heading">
              <p className="ranking-kicker">{t("ranking.label")}</p>
              <h1 className="ranking-title">{t("home.hallOfFame")}</h1>
              <p className="ranking-intro">{t("ranking.intro", { count: data?.total ?? 0 })}</p>
            </div>
            <div className="ranking-actions">
              <Link to={localized("/")} className="ranking-back">
                {t("ranking.back")}
              </Link>
              {isAuthenticated && myId != null && (
                <button type="button" id="rankingGoToMe" className="ranking-me-button" onClick={goToMe}>
                  {t("ranking.myPosition")}
                  {myPosition != null && <span className="ranking-me-rank">#{myPosition}</span>}
                </button>
              )}
            </div>
          </header>

          {podium.length > 0 && (
            <ol className="ranking-podium" aria-label={t("ranking.podium")}>
              {podium.map((entry) => (
                <li key={entry.id} className={`podium-step podium-${entry.rank}`}>
                  <PlayerCardButton user={entry} picture={avatarFor(entry)}>
                    <LeagueFrame league={entry.league} size="lg" className="podium-frame">
                      {look(entry)}
                    </LeagueFrame>
                  </PlayerCardButton>
                  <span className="podium-rank">#{entry.rank}</span>
                  <span className="podium-name">{entry.username}</span>
                  <span className="podium-points">
                    {formatPoints(entry.rankingPoints)} <span className="ranking-pts">{t("ranking.pts")}</span>
                  </span>
                  <span className="podium-plinth" aria-hidden="true" />
                </li>
              ))}
            </ol>
          )}

          {data && data.total === 0 ? (
            <p className="ranking-empty">{t("ranking.empty")}</p>
          ) : (
            <>
              <section className="ranking-filters" aria-label={t("ranking.filters")}>
                <div className="ranking-filters-top">
                  <label htmlFor="ranking-search" className="ranking-search">
                    <span className="visually-hidden">{t("ranking.searchLabel")}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                      <circle cx="11" cy="11" r="7" />
                      <path d="M20 20l-3.5-3.5" />
                    </svg>
                    <input
                      id="ranking-search"
                      type="search"
                      placeholder={t("ranking.searchPlaceholder")}
                      value={searchText}
                      onChange={(event) => {
                        setSearchText(event.target.value)
                        setPage(1)
                      }}
                    />
                  </label>
                  <label className="ranking-hide-zero">
                    <input
                      type="checkbox"
                      checked={hideZero}
                      onChange={(event) => {
                        setHideZero(event.target.checked)
                        setPage(1)
                      }}
                    />
                    {t("ranking.hideZero")}
                  </label>
                </div>
                <div className="ranking-league-wrap">
                  <div className="ranking-league-row" role="group" aria-label={t("ranking.leagueGroup")} style={{ "--chips": chips.length }}>
                    {chips.map(({ id, count }) => (
                      <button
                        key={id}
                        type="button"
                        className={`league-chip league-${id}`}
                        aria-pressed={league === id}
                        onClick={() => {
                          setLeague(league === id ? ALL_LEAGUES : id)
                          setPage(1)
                        }}
                      >
                        <span className="league-chip-name">
                          <LeagueGlyph league={id} />
                          {leagueName(id)}
                        </span>
                        <span className="league-chip-meta">
                          <span className="league-chip-range">{rangeText(id)}</span>
                          <span className="league-chip-count">{count}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </section>

              <section className="ranking-results" aria-label={t("ranking.label")}>
                <div className="ranking-results-bar">
                  <span>
                    <Trans
                      i18nKey="ranking.showing"
                      count={data?.count ?? 0}
                      values={{ range: from ? `${from}–${to}` : "0" }}
                      components={[<strong />]}
                    />
                  </span>
                  <label className="ranking-per-page">
                    {t("ranking.perPage")}
                    <select
                      value={perPage}
                      onChange={(event) => {
                        setPerPage(Number(event.target.value) || DEFAULT_PER_PAGE)
                        setPage(1)
                      }}
                    >
                      {PER_PAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="ranking-table">
                  <div className="ranking-table-head" aria-hidden="true">
                    <span>#</span>
                    <span>{t("ranking.colPlayer")}</span>
                    <span className="ranking-league-cell">{t("ranking.colLeague")}</span>
                    <span className="ranking-points">{t("ranking.colPoints")}</span>
                  </div>
                  {slice.length || !data ? (
                    <ol className="ranking-list">{slice.map((entry) => row(entry))}</ol>
                  ) : (
                    <div className="ranking-no-match">
                      <span className="ranking-no-match-title">{t("ranking.noMatch")}</span>
                      <span className="ranking-no-match-hint">{t("ranking.noMatchHint")}</span>
                      <button type="button" className="ranking-clear" onClick={clearFilters}>
                        {t("ranking.clearFilters")}
                      </button>
                    </div>
                  )}
                </div>

                {pageCount > 1 && (
                  <nav className="ranking-pages" aria-label={t("ranking.pagination")}>
                    <button
                      type="button"
                      className="ranking-page-button"
                      aria-label={t("ranking.previousPage")}
                      disabled={current <= 1}
                      onClick={() => setPage(current - 1)}
                    >
                      <Arrow back />
                    </button>
                    {pageNumbers(current, pageCount).map((number, index) =>
                      number === GAP ? (
                        <span key={`gap-${index}`} className="ranking-page-gap" aria-hidden="true">
                          {GAP}
                        </span>
                      ) : (
                        <button
                          key={number}
                          type="button"
                          className="ranking-page-button"
                          aria-label={t("ranking.pageN", { page: number })}
                          aria-current={number === current ? "page" : undefined}
                          onClick={() => setPage(number)}
                        >
                          {number}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      className="ranking-page-button"
                      aria-label={t("ranking.nextPage")}
                      disabled={current >= pageCount}
                      onClick={() => setPage(current + 1)}
                    >
                      <Arrow />
                    </button>
                  </nav>
                )}
              </section>
            </>
          )}

        </main>
      </div>
    </BaseLayout>
  )
}

export default Ranking
